import React, { useEffect, useRef, useState } from 'react';
import { colors as theme } from '../../theme';

const STEP = 0.5; // kg per minor tick — realistic scale precision without an unwieldy DOM
const TICK_SPACING = 14; // px between adjacent ticks
const MAJOR_EVERY = 2; // every 2nd tick (1kg) gets a label + taller mark

const colors = {
  bg: theme.bg,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
  primary: theme.gold,
};

/**
 * Horizontal scrub ruler for bodyweight recording — kg only. Uses the same
 * native CSS scroll-snap technique as the wheel pickers, just on the x-axis.
 * Tap the big number to switch to direct typed entry (precision fallback for
 * mouse/desktop use, where dragging a ruler is worse than typing).
 */
const WeightRuler = ({ value, onChange, min = 30, max = 300, isUnset = false }) => {
  const containerRef = useRef(null);
  const scrollTimeout = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const didMountEmit = useRef(false);
  const [typing, setTyping] = useState(false);
  const [typedValue, setTypedValue] = useState('');

  const steps = Math.round((max - min) / STEP);
  const clampedValue = Math.min(Math.max(value, min), max);
  const selectedIndex = Math.round((clampedValue - min) / STEP);

  // The ruler shows a default weight even when the form field is empty. Report
  // it up on mount so tapping Continue without scrubbing records that weight
  // instead of failing with "Weight: 30-300kg". Only when genuinely unset.
  useEffect(() => {
    if (didMountEmit.current || !isUnset) return;
    didMountEmit.current = true;
    onChange(clampedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typing) return;
    const targetLeft = selectedIndex * TICK_SPACING;
    if (Math.abs(el.scrollLeft - targetLeft) > 1) {
      isProgrammaticScroll.current = true;
      el.scrollTo({ left: targetLeft, behavior: 'auto' });
      requestAnimationFrame(() => { isProgrammaticScroll.current = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, typing]);

  const handleScroll = () => {
    if (isProgrammaticScroll.current) return;
    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const index = Math.round(el.scrollLeft / TICK_SPACING);
      const clampedIndex = Math.min(Math.max(index, 0), steps);
      const newValue = min + clampedIndex * STEP;
      if (newValue !== value) onChange(newValue);
    }, 120);
  };

  const commitTyped = () => {
    const parsed = parseFloat(typedValue);
    if (!isNaN(parsed)) {
      onChange(Math.min(Math.max(parsed, min), max));
    }
    setTyping(false);
  };

  return (
    <div style={{ textAlign: 'center' }}>
      {typing ? (
        <input
          type="number"
          inputMode="decimal"
          autoFocus
          value={typedValue}
          onChange={(e) => setTypedValue(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => { if (e.key === 'Enter') commitTyped(); }}
          style={{
            width: '160px',
            fontSize: '40px',
            fontWeight: 700,
            textAlign: 'center',
            background: colors.bg,
            border: `2px solid ${colors.primary}`,
            borderRadius: '8px',
            color: colors.text,
            padding: '4px 8px',
            outline: 'none',
          }}
        />
      ) : (
        <div
          onClick={() => { setTypedValue(String(clampedValue)); setTyping(true); }}
          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}
        >
          <span style={{ fontSize: '40px', fontWeight: 700, color: colors.text }}>
            {clampedValue.toFixed(1)}
          </span>
          <span style={{ fontSize: '18px', fontWeight: 600, color: colors.textMuted }}>kg</span>
        </div>
      )}
      <div style={{ position: 'relative', height: '64px', marginTop: '12px' }}>
        <div
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            alignItems: 'flex-end',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            cursor: 'grab',
          }}
          className="cipher-weight-ruler"
        >
          <div style={{ flex: `0 0 calc(50% - ${TICK_SPACING / 2}px)` }} />
          {Array.from({ length: steps + 1 }, (_, i) => {
            const tickValue = min + i * STEP;
            const isMajor = i % MAJOR_EVERY === 0;
            return (
              <div
                key={i}
                style={{
                  flex: `0 0 ${TICK_SPACING}px`,
                  scrollSnapAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  height: '100%',
                }}
              >
                {isMajor && Number.isInteger(tickValue) && (
                  <span style={{ fontSize: '10px', color: colors.textMuted, marginBottom: '4px' }}>
                    {tickValue}
                  </span>
                )}
                <div style={{
                  width: isMajor ? '2px' : '1px',
                  height: isMajor ? '20px' : '12px',
                  background: colors.border,
                }} />
              </div>
            );
          })}
          <div style={{ flex: `0 0 calc(50% - ${TICK_SPACING / 2}px)` }} />
        </div>
        {/* Center indicator — fixed, ignores pointer events */}
        <div style={{
          position: 'absolute',
          left: '50%',
          bottom: 0,
          width: '2px',
          height: '32px',
          background: colors.primary,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }} />
      </div>
      <style>{`.cipher-weight-ruler::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
};

export default WeightRuler;
